import { useEffect, useMemo, useRef, useState } from "react"
import type { Dispatch, RefObject, SetStateAction } from "react"

import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    streamingUtils: { createRoundedRectPath }
} = uiUtils

const BORDER_RADIUS_PX = 20
const BORDER_STROKE_WIDTH_PX = 1

type TCoachingChatProgressingMessageBorderHookParams = { isDone: boolean }

type TCoachingChatProgressingMessageBorderHookReturn = {
    shouldRender: boolean
    path: string
    size: Partial<DOMRect>
    messageRef: RefObject<HTMLDivElement>
}

function useCoachingChatStreamingMessageBorderHook({
    isDone
}: TCoachingChatProgressingMessageBorderHookParams): ReturnType<
    (params: TCoachingChatProgressingMessageBorderHookParams) => TCoachingChatProgressingMessageBorderHookReturn
> {
    const messageRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const [bubbleSize, setBubbleSize]: [Partial<DOMRect>, Dispatch<SetStateAction<Partial<DOMRect>>>] = useState<
        Partial<DOMRect>
    >({ width: 0, height: 0 })

    useEffect((): void => {
        if (!messageRef.current) return

        if (!isDone) {
            setBubbleSize({ width: 0, height: 0 })
            return
        }

        const { width, height }: DOMRect = messageRef.current.getBoundingClientRect()

        if (!width || !height) return

        setBubbleSize({ width, height })
    }, [isDone])

    const shouldRenderBorder: boolean = isDone && bubbleSize.width > 0 && bubbleSize.height > 0

    const borderPath: string = useMemo((): string => {
        if (!shouldRenderBorder) return String("")

        return createRoundedRectPath(bubbleSize.width, bubbleSize.height, BORDER_RADIUS_PX, BORDER_STROKE_WIDTH_PX / 2)
    }, [bubbleSize.height, bubbleSize.width, shouldRenderBorder])

    return {
        shouldRender: shouldRenderBorder,
        path: borderPath,
        size: bubbleSize,
        messageRef
    }
}

export { useCoachingChatStreamingMessageBorderHook }
export type { TCoachingChatProgressingMessageBorderHookParams, TCoachingChatProgressingMessageBorderHookReturn }
